import { ErrorCode } from '@tob/shared/api-constants';
import { ROUTES } from '@shopee-user/url';

// ==================== Enums ====================

export enum ThirdpartyType {
  Shopee = 1,
  Google = 2,
  Apple = 4,
  Facebook = 3,
}

export enum ThirdpartyBindingEntryPoint {
  Unknown = 0,
  AccountSettingPage = 1,
  SignupByOtpPage = 2,
  LoginByOtpPage = 3,
  LoginByPasswordPage = 4,
  ThirdPartyBindSharedService = 5,
  ReloginPage = 6,
}

export enum ThirdpartyAuthOperation {
  Bind = 'bind',
  Unbind = 'unbind',
  Login = 'login',
}

export enum ThirdPartyBindStatus {
  Bound = 'bound',
  Unbound = 'unbound',
}

// ==================== Types ====================

export type ThirdpartyLinkage = {
  thirdpartyType: ThirdpartyType;
  thirdpartyIdentity: string;
};

// ==================== Converters ====================

export const convertNumberToThirdpartyType = (value: number) => {
  switch (value) {
    case 1:
      return ThirdpartyType.Shopee;
    case 2:
      return ThirdpartyType.Google;
    case 3:
      return ThirdpartyType.Facebook;
    case 4:
      return ThirdpartyType.Apple;
    default:
      return null;
  }
};

// ==================== Error Maps ====================

export const THIRDPARTY_ERROR_MAP = {
  [ErrorCode.NotFound]: 'msg_auth_thirdparty_login_account_not_found', // the thirdparty doesn't bind to any account
  [ErrorCode.AccountDeleted]: 'msg_auth_account_suspended',
  [ErrorCode.RegionMismatch]: 'msg_auth_account_registered_another_region', // Account has been registered in another region, please try another one
  [ErrorCode.ToCLinked]: 'msg_auth_thirdparty_integration_toc_linked_error', // This credential is already linked to a Shopee account and cannot be used for third-party login. Please unlink it before trying again.
  [ErrorCode.ThirdpartyBound]:
    'msg_auth_thirdparty_integration_thirdparty_bound_error', // Your account is already linked to another {{third_party_method}} login. To continue, please log in using the linked third-party account.
  [ErrorCode.ThirdpartyFraudBindShopee]: 'msg_auth_system_error', // An error has occurred. Please try again. (Rule Engine blocks binding)
  [ErrorCode.ThirdpartyNoPermission]: 'msg_auth_system_error', // An error has occurred. Please try again. (client id is incorrect)
  [ErrorCode.ThirdpartyTokenInvalid]: 'msg_auth_system_error', // An error has occurred. Please try again. (oauth authentication failed)
  [ErrorCode.ThirdpartyExisted]:
    'msg_auth_thirdparty_integration_thirdparty_existed_error', // This {{third_party_method}} account is already linked to another account. Please make sure you are using the correct login method.
  [ErrorCode.TobLinked]: 'msg_auth_tob_linked_error', // This Shopee account is already linked to another existing account. Please make sure you are using the correct login method.
  [ErrorCode.BindThirdpartyTokenInvalid]: 'msg_auth_system_error', // An error has occurred. Please try again. (bind_third_party_token is invalid)
  [ErrorCode.ThirdpartyFraudBindGoogle]: 'msg_auth_system_error', // An error has occurred. Please try again. (Rule Engine blocks binding)
  [ErrorCode.ThirdpartyFraudBindFacebook]: 'msg_auth_system_error', // An error has occurred. Please try again. (Rule Engine blocks binding)
  [ErrorCode.ThirdpartyFraudBindApple]: 'msg_auth_system_error', // An error has occurred. Please try again. (Rule Engine blocks binding)
  [ErrorCode.PhoneNotExist]: 'msg_auth_phone_is_not_registered', // No account found linked with this phone number.
  default: 'msg_auth_thirdparty_general_error_try_later', // System error, please try again
};

// ==================== Redirect URIs ====================

export const THIRDPARTY_AUTH_PROVIDER_REDIRECT_URI: Record<
  ThirdpartyBindingEntryPoint,
  string
> = {
  [ThirdpartyBindingEntryPoint.Unknown]: window.location.origin,
  [ThirdpartyBindingEntryPoint.AccountSettingPage]:
    window.location.origin + ROUTES.USER_ACCOUNT_LINKAGE,
  [ThirdpartyBindingEntryPoint.SignupByOtpPage]:
    window.location.origin + ROUTES.LOGIN_BY_THIRD_PARTY,
  [ThirdpartyBindingEntryPoint.LoginByOtpPage]:
    window.location.origin + ROUTES.LOGIN_BY_THIRD_PARTY,
  [ThirdpartyBindingEntryPoint.LoginByPasswordPage]:
    window.location.origin + ROUTES.LOGIN_BY_THIRD_PARTY,
  [ThirdpartyBindingEntryPoint.ThirdPartyBindSharedService]:
    window.location.origin + ROUTES.THIRD_PARTY_BINDING_SHARED_SERVICE,
  [ThirdpartyBindingEntryPoint.ReloginPage]:
    window.location.origin + ROUTES.LOGIN_BY_THIRD_PARTY,
};
